import { Asset, isEveOfHoliday, isHolidayDate, isVacationDate } from '../../models/asset.model';
import {
  dayHoursCloseToMinutes,
  dayHoursOpenToMinutes,
  isDayClosed,
} from '../location/opening-hours.utils';
import { dayOfWeekMon0 } from './schedule.utils';

/** Parses `YYYY-MM-DD` as local midnight — avoids UTC `Date` string pitfalls. */
export function dateFromDateStr(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

/** Calendar weekday index 0 = Mon … 6 = Sun, derived from `dateStr` only. */
export function weekdayIndexMon0(dateStr: string): number {
  return dayOfWeekMon0(dateFromDateStr(dateStr));
}

/**
 * Maps a calendar date to the pricing/schedule day index (0=Mon … 6=Sun).
 * Holidays → 6 (Dom/Fest column); eves → 5 (Sáb/Visp column).
 * Weekday is always taken from `dateStr` so it stays aligned with calendar cells.
 */
export function logicalScheduleDay(date: Date, dateStr: string, space: Asset): number {
  void date;
  const dow = weekdayIndexMon0(dateStr);
  if (isHolidayDate(dateStr, space)) return 6;
  if (isEveOfHoliday(dateStr, space)) return 5;
  return dow;
}

/** Operating window for a concrete date (respects holiday/eve day mapping). */
export function dayHoursForDate(
  space: Asset,
  date: Date,
  dateStr: string,
): { open: string; close: string } {
  void date;
  if (isVacationDate(dateStr, space)) {
    return { open: '00:00', close: '00:00' };
  }
  const logicalDay = logicalScheduleDay(dateFromDateStr(dateStr), dateStr, space);
  const dayHours = Array.isArray(space.weeklyHours) && space.weeklyHours.length === 7
    ? space.weeklyHours[logicalDay]
    : null;
  if (dayHours && isDayClosed(dayHours)) {
    return { open: '00:00', close: '00:00' };
  }
  return {
    open:  dayHours?.open  ?? space.openingTime ?? '08:00',
    close: dayHours?.close ?? space.closingTime ?? '22:00',
  };
}

export interface DayOperatingWindow {
  open: string;
  close: string;
  closed: boolean;
  openMin: number;
  closeMin: number;
  totalMin: number;
}

/** Minutes-based operating window for occupancy / heatmap (handles closed + midnight close). */
export function dayOperatingWindowForDate(
  space: Asset,
  date: Date,
  dateStr: string,
): DayOperatingWindow {
  const { open, close } = dayHoursForDate(space, date, dateStr);
  const closed = isDayClosed({ open, close });
  const openMin  = dayHoursOpenToMinutes(open);
  const closeMin = dayHoursCloseToMinutes(close, open);
  const totalMin = closed ? 0 : Math.max(0, closeMin - openMin);
  return { open, close, closed, openMin, closeMin, totalMin };
}
